import { ReactNode, useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '~/layouts/Header';
import { Notification } from '~/components/Global/Notification';
import { StyledContent, StyledMain } from '~/layouts/Layout/Layout.style';
import { DEVICE, useDevice } from '~/utils/hook/useDevice';
import { useDisplay } from '~/utils/hook/useDisplay';
import { routePath, resolveInternetPageSize } from '~/utils/constants';
import { ResourceSearch } from '~/components/Global/ResourceSearch';
import { LatestNewsFilterProvider } from '~/components/LatestNews/LatestNewsFilterContext';
import { FileSearch } from '~/components/Global/FileSearch';
import { Sidebar } from '~/layouts/Sidebar';
import { useGetNotificationMessagesQuery } from '~/api/oneBoxV2';
import { useSelector } from 'react-redux';
import { getterNotifyMessages } from '~/store/notify/notifyGetter';
import {
  getterClientResourcesPayload,
  getterClientResourceFilter,
} from '~/store/resource/resourceGetter';
import {
  useGetInternetResourcesQuery,
  useLazyGetResourcesQuery,
  useDropDownListQuery,
  useInternetDropDownListQuery,
} from '~/api/oneBoxV3';
import { useDownload } from '~/utils/hook/useDownload';
import { useAutoExtractAllEffect } from '~/utils/hook/useAutoExtractEffect';
import { TDownloadList, TProgressList } from '~/store/resource/resource.type';
import { RootState, useAppDispatch } from '~/store';
import {
  setDownloadList,
  setProgressList,
  setSearchCondition,
} from '~/store/resource';
import { setSearchCondition as setInterSearchCondition } from '~/store/internetResource';
import { getterInternetResourcesPayload } from '~/store/internetResource/internetResourceGetter';
import { isWeb } from '~/utils/helper/device';
import {
  getNaniClubOidcAuthUrl,
  shouldUseNaniClubOidc,
} from '~/utils/helper/naniClub';
import { BaseDialog } from '~/components/Global/Dialog/BaseDialog';
// import BitrixService from '~/components/BitrixComponent/BitrixComponent';
import { setShowLoginDialog } from '~/store/user';
import { Typography } from '@mui/material';
import {
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
  BaseButton,
} from '~/components/Global/Button';
import { SIDEBAR, SearchCategory } from '~/types/enum';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import { setLocalSotrage } from '~/components/Global/ResourceSearch';
import { orderHighSchoolSidebarBySubject } from '~/utils/highSchoolSidebarBySubject';
import { useOfflineMode } from '~/contexts/OfflineModeContext';
import { OfflineBanner } from '~/components/Offline';

type TLayoutProps = {
  children: ReactNode;
};

export const Layout = (props: TLayoutProps) => {
  const { children } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOffline } = useOfflineMode();
  const { device } = useDevice();
  const {
    getDownloadList,
    downloadProgressListener,
    downloadListChangeListener,
    consoleListener,
  } = useDownload();
  const { useOutsideAction } = useDisplay();
  const messages = useSelector(getterNotifyMessages);
  const resourcePayload = useSelector(getterClientResourcesPayload);
  const internetResourcePayload = useSelector(getterInternetResourcesPayload);
  const { eduCodes, resources1, subjects, grades, years } = useSelector(
    getterClientResourceFilter,
  );
  const [isSidebarShown, setIsSideBarShown] = useState<boolean>(false);
  const [sidebarRef, setSidebarRef] = useState<HTMLUListElement | undefined>();
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | undefined>();

  // ========== Sidebar Dropdown 資料載入邏輯（從 Sidebar 移到 Layout）==========
  /** 需先選學制＋科目，才顯示左側資源（包含最新消息） */
  const shouldFetchDropdown = !!(eduCodes && subjects);

  const { data: dropdownData, isFetching: isDropdownFetching } =
    useDropDownListQuery(
      {
        isLaunch: true,
        categories: [SearchCategory.SCHOOL],
        resourceProps: ['resources1'],
        search: {
          eduCodes: eduCodes || undefined,
          subjects,
          grades,
          years,
        },
      },
      { skip: isOffline || !shouldFetchDropdown },
    );

  const { data: internetDropdownData, isFetching: isInternetDropdownFetching } =
    useInternetDropDownListQuery(
      {
        isLaunch: true,
        categories: [SearchCategory.SCHOOL, SearchCategory.PUBLIC],
        internetResourceProps: [
          'resources1',
          'eduCodes',
          'years',
          'grades',
          'subjects',
        ],
        search: {
          eduCodes: eduCodes || undefined,
          subjects,
          grades,
          years,
        },
      },
      { skip: isOffline || !shouldFetchDropdown },
    );

  const isSidebarFetching = isDropdownFetching || isInternetDropdownFetching;

  // 計算 sidebar 列表
  const filteredSidebarList = useMemo(() => {
    if (!shouldFetchDropdown) return [];

    let sideBarCol = [
      ...(dropdownData?.data?.uniqResourceProps.resources1 || []),
    ];
    const internetResourceList =
      internetDropdownData?.data?.uniqInternetResourceProps.resources1 || [];
    if (internetResourceList.length > 0) {
      sideBarCol = [...sideBarCol, internetResourceList[0]];
    }

    // 依照不同學制排序與過濾
    const orderByEduCode = (
      col: string[],
      eduCode: string | undefined,
    ): string[] => {
      const commonItemsMap: Record<string, string[]> = {
        [resourceEduCode.E]: [
          '電子書',
          '題庫光碟',
          '課程計畫',
          '授課教案',
          '備課資源',
          '教材資源',
          '卷類資源',
          '多媒體',
          '網路資源',
        ],
        [resourceEduCode.J]: [
          '電子書',
          '輔材電子書',
          '題庫光碟',
          '備課素材',
          '卷類資源',
          '教學PPT',
          '課程計畫與教案',
          '多媒體',
          '期刊專區',
          '網路資源',
        ],
        // 國文欄皆「V」者（不含教材電子檔、大考及段考題目）；其餘依科目在 orderHighSchoolSidebarBySubject
        [resourceEduCode.H]: [
          '題庫光碟',
          '教學大補帖光碟',
          '教學PPT',
          '課程計畫',
          '影音資源',
          '多元選修',
          '期刊',
          '網路資源',
        ],
      };

      const orderMap: Record<string, string[]> = {
        [resourceEduCode.E]: [
          '電子書',
          '題庫光碟',
          '課程計畫',
          '授課教案',
          '備課資源',
          '教材資源',
          '卷類資源',
          '多媒體',
          '網路資源',
          '教學PPT',
          '教學簡報',
        ],
        [resourceEduCode.J]: [
          '電子書',
          '輔材電子書',
          '題庫光碟',
          '備課素材',
          '卷類資源',
          '教學PPT',
          '課程計畫與教案',
          '多媒體',
          '期刊專區',
          '網路資源',
        ],
        [resourceEduCode.H]: [
          '題庫光碟',
          '教學大補帖光碟',
          '教材電子檔',
          '教材PDF',
          '教材WORD',
          '教學PPT',
          '課程計畫',
          '教材圖檔',
          '影音資源',
          '實境解題',
          '數位教學學習單',
          '多元選修',
          '大考及段考題目',
          '升學-大考',
          '期刊',
          '網路資源',
          '心智圖',
          '教學遊戲',
        ],
      };

      const targetOrder =
        eduCode && orderMap[eduCode]
          ? orderMap[eduCode]
          : Object.values(SIDEBAR);

      const commonItems =
        eduCode && commonItemsMap[eduCode] ? commonItemsMap[eduCode] : [];
      const ordered: string[] = [];

      for (const key of targetOrder) {
        // 如果是共通品項，或是該品項有資料(在 col 中)，就顯示
        if (commonItems.includes(key) || col.includes(key)) {
          ordered.push(key);
        }
      }
      for (const item of col) {
        if (!ordered.includes(item)) {
          ordered.push(item);
        }
      }
      return ordered;
    };

    const sortedSideBarCol = orderByEduCode(sideBarCol, eduCodes);

    let result = sortedSideBarCol;
    if (eduCodes === resourceEduCode.H && subjects) {
      const bySubject = orderHighSchoolSidebarBySubject(
        subjects,
        sortedSideBarCol,
      );
      if (bySubject) result = bySubject;
    }

    // 過濾高中的 ebook
    if (eduCodes === resourceEduCode.H) {
      return result.filter(item => (item as SIDEBAR) !== SIDEBAR.ebook);
    }
    return result;
  }, [
    dropdownData,
    internetDropdownData,
    eduCodes,
    subjects,
    shouldFetchDropdown,
  ]);

  /** 資源側欄最上方固定「最新消息」（僅 /resource） */
  const sidebarListWithLatest = useMemo(
    () =>
      shouldFetchDropdown ? [SIDEBAR.latestNews, ...filteredSidebarList] : [],
    [shouldFetchDropdown, filteredSidebarList],
  );

  // ========== Resources Query ==========
  const [triggerGetResources] = useLazyGetResourcesQuery();
  const [hasTriggeredResourcesQuery, setHasTriggeredResourcesQuery] =
    useState(false);
  // 統一處理：自動選擇預設 resources1 + 觸發資源查詢
  // 使用 useLazyQuery 可以在 useEffect 中直接用計算後的值觸發，不需等待下一個 render cycle
  useEffect(() => {
    if (isOffline) return;
    if (isSidebarFetching) return;
    if (sidebarListWithLatest.length === 0) return;

    const needAutoSelect = !sidebarListWithLatest.includes(resources1 || '');
    const resolveDefaultResources1 = (): string => {
      // 需求：第一次（從未選過 resources1）在選完學制/科目後，預設 focus「電子書」
      // 之後（有選過）就沿用使用者最後選的（localStorage）
      const storedValue = localStorage.getItem('resourceSearchKey');
      const stored =
        storedValue && storedValue.trim() !== ''
          ? (JSON.parse(storedValue) as { [key: string]: string })
          : {};
      const storedResources1 = String(stored?.resources1 || '').trim();

      const desired =
        storedResources1.length > 0 ? storedResources1 : SIDEBAR.ebook;

      // 如果 desired 不在當前 sidebar（例如高中沒有 ebook），就退回第一個非最新消息的項目
      if (sidebarListWithLatest.includes(desired)) return desired;
      const fallback =
        sidebarListWithLatest.find(
          x => (x as SIDEBAR) !== SIDEBAR.latestNews,
        ) ?? sidebarListWithLatest[0];
      return fallback;
    };

    const targetResources1 = needAutoSelect
      ? resolveDefaultResources1()
      : resources1;

    const resolveSidebarPageSize = (r1: string) => {
      if ((r1 as SIDEBAR) === SIDEBAR.latestNews) return 5;
      if (
        r1 &&
        (r1 as SIDEBAR) !== SIDEBAR.ebook &&
        (r1 as SIDEBAR) !== SIDEBAR.tiku
      ) {
        return (r1 as SIDEBAR) === SIDEBAR.internet ? 100 : 4;
      }
      return 30;
    };

    if (needAutoSelect && targetResources1) {
      const size = resolveSidebarPageSize(targetResources1);
      dispatch(
        setSearchCondition({
          resources1: targetResources1,
          pageSize: size,
          page: 0,
        }),
      );
      dispatch(
        setInterSearchCondition({
          resources1: targetResources1,
          pageSize: resolveInternetPageSize(targetResources1),
          page: 0,
          eduCodes: eduCodes ?? 'all',
          years: years ?? 'all',
          subjects: subjects ?? 'all',
          grades: grades ?? 'all',
        }),
      );
      const storedValue = localStorage.getItem('resourceSearchKey');
      const resourceSearchKey: { [key: string]: string } =
        storedValue && storedValue.trim() !== ''
          ? (JSON.parse(storedValue) as { [key: string]: string })
          : {};
      setLocalSotrage({ ...resourceSearchKey, resources1: targetResources1 });
    }

    if (targetResources1 === SIDEBAR.latestNews) {
      setHasTriggeredResourcesQuery(true);
      return;
    }

    if (targetResources1) {
      void triggerGetResources({
        ...resourcePayload,
        search: {
          ...resourcePayload.search,
          resources1: targetResources1,
        },
      });
      setHasTriggeredResourcesQuery(true);
    }
  }, [
    isSidebarFetching,
    sidebarListWithLatest,
    resources1,
    resourcePayload,
    dispatch,
    triggerGetResources,
    isOffline,
  ]);

  // 未選年級時改由 Resource 依各年級合併查詢，避免單次查詢漏掉 GGB 等依年級標記的資料
  const skipInternetResourcesDefaultQuery =
    !hasTriggeredResourcesQuery ||
    (resources1 === SIDEBAR.internet &&
      (!grades || grades === '' || grades === 'all'));

  useGetInternetResourcesQuery(internetResourcePayload, {
    refetchOnMountOrArgChange: true,
    skip:
      isOffline ||
      resources1 === SIDEBAR.latestNews ||
      skipInternetResourcesDefaultQuery,
  });

  useGetNotificationMessagesQuery(undefined, { skip: isOffline });

  // 下載完成後的解壓觸發：掛在不隨換頁 unmount 的 Layout，
  // 使用者切換科目或離開頁面時仍能自動接續安裝
  useAutoExtractAllEffect();

  useEffect(() => {
    if (isOffline) {
      setIsSideBarShown(false);
      return;
    }
    if (device === DEVICE.DESKTOP) {
      setIsSideBarShown(true);
    } else {
      setIsSideBarShown(false);
    }
  }, [device, isOffline]);

  useEffect(() => {
    void (async () => {
      if (!isWeb) {
        const downloadList = (await getDownloadList()) as TDownloadList;
        dispatch(setDownloadList(downloadList));
      }
    })();
  }, []);

  useEffect(() => {
    if (!isWeb) {
      downloadProgressListener(progressMap => {
        dispatch(setProgressList(progressMap as TProgressList));
      });

      consoleListener();

      downloadListChangeListener(
        ({ downloadList }: { downloadList: TDownloadList }) => {
          dispatch(setDownloadList(downloadList));
        },
      );
    }
  }, []);

  useOutsideAction({
    target: sidebarRef,
    whiteList: [buttonRef],
    action: () => {
      if (device === DEVICE.DESKTOP || !isSidebarShown) return;
      setIsSideBarShown(false);
    },
  });

  const showLoginDialog = useSelector(
    (state: RootState) => state.user.showLoginDialog,
  );
  const action = () => {
    return (
      <>
        <BaseButton
          variant={BASE_BUTTON_VARIANT.OUTLINED}
          color={BASE_BUTTON_COLOR.PRIMARY}
          onClick={() => dispatch(setShowLoginDialog(false))}
          text="取消"
        />
        <BaseButton
          variant={BASE_BUTTON_VARIANT.FILLED}
          color={BASE_BUTTON_COLOR.PRIMARY}
          onClick={() => {
            if (shouldUseNaniClubOidc()) {
              window.location.href = getNaniClubOidcAuthUrl();
            } else {
              navigate(routePath.login);
              dispatch(setShowLoginDialog(false));
            }
          }}
          text="前往登入"
        />
      </>
    );
  };

  return (
    <>
      <Header
        setButtonRef={setButtonRef}
        setIsSideBarShown={setIsSideBarShown}
      />
      {isOffline && <OfflineBanner />}
      {/* <AiPopup /> */}
      <StyledContent>
        {!isOffline && pathname === routePath.resource && isSidebarShown && (
          <Sidebar
            setSidebarRef={setSidebarRef}
            sidebarList={sidebarListWithLatest}
            isFetching={isSidebarFetching}
          />
        )}
        <StyledMain
          path={pathname}
          isSideBarShown={isSidebarShown}
          isOffline={isOffline}
        >
          {!isOffline && pathname !== routePath.login && (
            <Notification notificationList={messages} />
          )}
          {!isOffline &&
          pathname === routePath.resource &&
          resources1 === SIDEBAR.latestNews ? (
            <LatestNewsFilterProvider>
              <ResourceSearch />
              {children}
            </LatestNewsFilterProvider>
          ) : (
            <>
              {!isOffline &&
                pathname === routePath.resource &&
                resources1 !== SIDEBAR.latestNews && <ResourceSearch />}
              {pathname === routePath.file && <FileSearch />}
              {children}
            </>
          )}
        </StyledMain>
      </StyledContent>
      {/* <BitrixService func={undefined} /> */}
      {showLoginDialog && (
        <BaseDialog
          open={showLoginDialog}
          handleClose={() => {
            dispatch(setShowLoginDialog(false));
          }}
          action={action()}
          title="尚未登入"
        >
          <Typography
            variant="h4"
            component={'p'}
            sx={{ minWidth: '600px', fontWeight: 500 }}
          >
            登入後才能進行下載，是否立即登入？
          </Typography>
        </BaseDialog>
      )}
    </>
  );
};
